import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Wallet, ArrowLeftRight, FileText, ClipboardList, Users, Gauge, Sliders, Landmark, ShieldCheck, LogOut, UserCog, Building2, Home, CreditCard, FileSignature, KeyRound, Gift, Mail } from "lucide-react";

const menus = {
  client: [
    { to: "/portal", label: "Dashboard", icon: LayoutGrid, end: true },
    { to: "/portal/holdings", label: "Holdings", icon: Wallet },
    { to: "/portal/transactions", label: "Transactions", icon: ArrowLeftRight },
    { to: "/portal/deposit", label: "Deposit via Stripe", icon: CreditCard },
    { to: "/portal/requests", label: "Deposits & Withdrawals", icon: ClipboardList },
    { to: "/portal/referrals", label: "My Referrals", icon: Gift },
    { to: "/portal/documents", label: "Legal Documents", icon: FileSignature },
    { to: "/portal/statements", label: "Statements", icon: FileText },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/admin/applications", label: "Applications", icon: ClipboardList },
    { to: "/admin/clients", label: "Clients", icon: Users },
    { to: "/admin/requests", label: "Deposit / Withdraw Queue", icon: ArrowLeftRight },
    { to: "/admin/legacy", label: "Legacy Onboarding", icon: FileText },
  ],
  super: [
    { to: "/super", label: "Business Metrics", icon: Gauge, end: true },
    { to: "/super/applications", label: "Applications", icon: ClipboardList },
    { to: "/super/access-requests", label: "Access Requests", icon: Mail },
    { to: "/super/onboard", label: "Onboard / Deposit", icon: Landmark },
    { to: "/super/clients", label: "Clients", icon: Users },
    { to: "/super/nav", label: "NAV Entry", icon: Landmark },
    { to: "/super/fees", label: "Fee Schedules", icon: Sliders },
    { to: "/super/corporate", label: "Corporate Ledger", icon: Building2 },
    { to: "/super/users", label: "Users & Roles", icon: UserCog },
    { to: "/super/gate", label: "Site Access Codes", icon: KeyRound },
    { to: "/super/referrals", label: "Referrals", icon: Gift },
    { to: "/super/mt5", label: "Trading Desk (MT5)", icon: Landmark },
    { to: "/super/audit", label: "Audit Log", icon: ShieldCheck },
  ],
};

const titles = { client: "Client Portal", admin: "Admin Console", super: "Super Admin" };

export default function PortalShell({ variant, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = menus[variant];
  const loc = useLocation();
  return (
    <div className="min-h-screen grid grid-cols-[240px_1fr]">
      <aside className="border-r border-border bg-card/40 flex flex-col">
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 border border-primary/60 flex items-center justify-center">
              <span className="gold font-mono text-xs">P</span>
            </div>
            <div className="text-sm">Piplight</div>
          </div>
          <div className="kicker text-[10px]">{titles[variant]}</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              data-testid={`side-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm border-l-2 ${isActive ? "border-primary text-foreground bg-secondary/60" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon size={16} strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="text-xs text-muted-foreground mb-1">Signed in</div>
          <div className="text-sm truncate">{user?.name}</div>
          <div className="text-xs text-muted-foreground font-mono truncate">{user?.email}</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button data-testid="btn-home" onClick={() => nav("/")}
              className="text-xs border border-border py-1.5 hover:border-primary/60 flex items-center justify-center gap-1">
              <Home size={12} /> Site
            </button>
            <button data-testid="btn-logout" onClick={() => { logout(); nav("/"); }}
              className="text-xs border border-border py-1.5 hover:border-destructive/60 flex items-center justify-center gap-1">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </aside>
      <div className="flex flex-col">
        <header className="border-b border-border h-14 px-6 flex items-center justify-between">
          <div className="kicker">{loc.pathname}</div>
          <div className="font-mono text-xs text-muted-foreground">{new Date().toUTCString()}</div>
        </header>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
